import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect } from 'react';
import { Grid3x3, Plus, Trash2, Save, Instagram, Youtube, Facebook, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'tiktok', label: 'TikTok', icon: Grid3x3 },
  { value: 'pinterest', label: 'Pinterest', icon: Grid3x3 },
];

const SocialGridEditor = () => {
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({
    title: 'follow our journey on IG',
    platform: 'instagram',
    handle: '',
    profile_url: '',
    follower_count: '',
    images: [],
    is_active: true,
  });

  useEffect(() => { fetchGrid(); }, []);

  const fetchGrid = async () => {
    try {
      const res = await axios.get(`${API}/social-grid`);
      if (res.data) {
        setGrid(res.data);
        setForm({
          title: res.data.title || '',
          platform: res.data.platform || 'instagram',
          handle: res.data.handle || '',
          profile_url: res.data.profile_url || '',
          follower_count: res.data.follower_count || '',
          images: res.data.images || [],
          is_active: res.data.is_active ?? true,
        });
      }
    } catch (err) {
      // No grid exists yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.handle) {
      toast.error('Please enter your social media handle');
      return;
    }
    setSaving(true);
    try {
      if (grid) {
        const res = await axios.put(`${API}/social-grid`, form);
        setGrid(res.data);
        toast.success('Social grid updated');
      } else {
        const res = await axios.post(`${API}/social-grid`, form);
        setGrid(res.data);
        toast.success('Social grid created');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!grid) return;
    try {
      await axios.delete(`${API}/social-grid`);
      setGrid(null);
      setForm({
        title: 'follow our journey on IG',
        platform: 'instagram',
        handle: '',
        profile_url: '',
        follower_count: '',
        images: [],
        is_active: true,
      });
      toast.success('Social grid deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleImageChange = (index, url) => {
    const newImages = [...form.images];
    if (url) {
      newImages[index] = url;
    } else {
      newImages.splice(index, 1);
    }
    setForm({ ...form, images: newImages });
  };

  const addImageSlot = () => {
    if (form.images.length >= 9) {
      toast.error('Maximum 9 images');
      return;
    }
    setForm({ ...form, images: [...form.images, ''] });
  };

  const IMPORT_PLATFORMS = {
    // instagram: { endpoint: 'import-instagram', label: 'Instagram', color: 'pink', defaultTitle: 'follow our journey on IG' },
    youtube: { endpoint: 'import-youtube', label: 'YouTube', color: 'red', defaultTitle: 'watch our latest videos' },
    tiktok: { endpoint: 'import-tiktok', label: 'TikTok', color: 'cyan', defaultTitle: 'check out our TikToks' },
  };

  const handleImport = async () => {
    const handle = form.handle?.trim()?.replace(/^@/, '');
    if (!handle) {
      toast.error(`Enter your ${form.platform} handle first`);
      return;
    }
    const config = IMPORT_PLATFORMS[form.platform];
    if (!config) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/social-grid/${config.endpoint}`, { username: handle });
      const d = res.data;
      setForm(prev => ({
        ...prev,
        handle: `@${d.username}`,
        profile_url: d.profile_url,
        follower_count: d.follower_count,
        title: prev.title || config.defaultTitle,
        images: d.images?.length ? d.images.slice(0, 9) : prev.images,
      }));
      if (d.message) {
        toast.success(d.message, { duration: 5000 });
      } else {
        toast.success(`Imported ${d.images.length} images from @${d.username}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to import';
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-grid-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Social Media Grid</h2>
          <p className="text-sm text-[#A1A1AA] mt-1">Showcase your social media feed on your profile</p>
        </div>
        {grid && (
          <Button
            data-testid="delete-social-grid-btn"
            variant="outline"
            size="sm"
            onClick={handleDelete}
            className="text-red-400 border-red-400/30 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        )}
      </div>

      <div className="space-y-4 bg-[#1E1E1E] rounded-xl p-5 border border-[#27272A]">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A1AA] text-sm">Platform</Label>
            <select
              data-testid="social-grid-platform"
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              className="w-full mt-1 bg-[#0a0a0a] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm"
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-sm">Handle</Label>
            <Input
              data-testid="social-grid-handle"
              value={form.handle}
              onChange={(e) => setForm({ ...form, handle: e.target.value })}
              placeholder="@yourhandle"
              className="mt-1 bg-[#0a0a0a] border-[#27272A] text-white"
            />
          </div>
        </div>

        <div>
          <Label className="text-[#A1A1AA] text-sm">Title</Label>
          <Input
            data-testid="social-grid-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="follow our journey on IG"
            className="mt-1 bg-[#0a0a0a] border-[#27272A] text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[#A1A1AA] text-sm">Profile URL</Label>
            <Input
              data-testid="social-grid-url"
              value={form.profile_url}
              onChange={(e) => setForm({ ...form, profile_url: e.target.value })}
              placeholder="https://instagram.com/yourhandle"
              className="mt-1 bg-[#0a0a0a] border-[#27272A] text-white"
            />
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-sm">Follower Count</Label>
            <Input
              data-testid="social-grid-followers"
              value={form.follower_count}
              onChange={(e) => setForm({ ...form, follower_count: e.target.value })}
              placeholder="28.2K followers"
              className="mt-1 bg-[#0a0a0a] border-[#27272A] text-white"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-[#A1A1AA] text-sm">Grid Images (up to 9)</Label>
            <div className="flex items-center gap-2">
              {form.images.length > 0 && (
                <Button
                  data-testid="clear-grid-images-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setForm({ ...form, images: [] })}
                  className="text-red-400 border-red-400/30 hover:bg-red-500/10 gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Clear All
                </Button>
              )}
              {IMPORT_PLATFORMS[form.platform] ? (
                <Button
                  data-testid="import-social-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleImport}
                  disabled={importing || !form.handle}
                  className={`gap-1 ${
                    form.platform === 'instagram' ? 'text-pink-400 border-pink-400/30 hover:bg-pink-500/10' :
                    form.platform === 'youtube' ? 'text-red-400 border-red-400/30 hover:bg-red-500/10' :
                    form.platform === 'tiktok' ? 'text-cyan-400 border-cyan-400/30 hover:bg-cyan-500/10' :
                    'text-violet-400 border-violet-400/30 hover:bg-violet-500/10'
                  }`}
                >
                  {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  {importing ? 'Importing...' : `Import from ${IMPORT_PLATFORMS[form.platform].label}`}
                </Button>
              ) : (
                form.platform !== 'instagram' && (
                  <span className="text-xs text-[#71717A] italic">Auto-import not available for {PLATFORMS.find(p => p.value === form.platform)?.label || form.platform}</span>
                )
              )}
              {form.images.length < 9 && (
                <Button
                  data-testid="add-grid-image-btn"
                  variant="outline"
                  size="sm"
                  onClick={addImageSlot}
                  className="text-violet-400 border-violet-400/30 hover:bg-violet-500/10"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Image
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative group">
                {img && (img.startsWith('http') || img.startsWith('/api/uploads/')) ? (
                  <div className="relative aspect-square rounded-lg overflow-hidden border border-[#27272A]">
                    <img src={resolveMediaUrl(img)} alt={`Grid ${i+1}`} className="w-full h-full object-cover" referrerPolicy="no-referrer"
                      onError={(e) => { e.target.src = ''; e.target.parentElement.classList.add('bg-[#27272A]'); }} />
                    <button onClick={() => handleImageChange(i, null)}
                      className="absolute top-1 right-1 bg-black/60 text-red-400 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <ImageUpload
                    value={img}
                    onChange={(url) => handleImageChange(i, url)}
                    label={`grid-image-${i}`}
                  />
                )}
              </div>
            ))}
            {form.images.length === 0 && (
              <p className="text-[#71717A] text-sm col-span-3 text-center py-4">
                No images added yet. Click "Add Image" to start.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="social-grid-active"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-[#27272A] bg-[#0a0a0a]"
            />
            <span className="text-sm text-[#A1A1AA]">Show on public profile</span>
          </label>
        </div>
      </div>

      <Button
        data-testid="save-social-grid-btn"
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-violet-600 hover:bg-violet-700 text-white"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : grid ? 'Update Social Grid' : 'Create Social Grid'}
      </Button>
    </div>
  );
};

export default SocialGridEditor;
